import { useEffect, useId, useSyncExternalStore, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertIcon, CheckCircleIcon, InfoIcon, XIcon } from './icons.js'

/**
 * Toasts and a confirm dialog.
 *
 * Toasts are a tiny module-level store rather than a context provider, so any
 * mutation's `onSuccess` can call `toast()` without threading a hook through
 * it. `<Toaster />` is mounted once per shell.
 */

type ToastKind = 'ok' | 'error' | 'info'
interface ToastItem {
  id: number
  kind: ToastKind
  title: string
  body?: string
}

let items: ToastItem[] = []
let seq = 0
const listeners = new Set<() => void>()
const emit = (): void => listeners.forEach((listener) => listener())

export function toast(kind: ToastKind, title: string, body?: string): void {
  const id = ++seq
  items = [...items, { id, kind, title, body }].slice(-4)
  emit()
  window.setTimeout(() => dismiss(id), kind === 'error' ? 7000 : 4000)
}

function dismiss(id: number): void {
  items = items.filter((item) => item.id !== id)
  emit()
}

export function Toaster(): ReactNode {
  const current = useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => items,
  )

  return createPortal(
    <div className="toaster" aria-live="polite">
      {current.map((item) => {
        const Icon = item.kind === 'ok' ? CheckCircleIcon : item.kind === 'error' ? AlertIcon : InfoIcon
        return (
          <div key={item.id} className={`toast toast--${item.kind}`} role={item.kind === 'error' ? 'alert' : 'status'}>
            <Icon size={17} />
            <div className="toast__body">
              <strong>{item.title}</strong>
              {item.body && <span>{item.body}</span>}
            </div>
            <button type="button" className="icon-btn" aria-label="Dismiss" onClick={() => dismiss(item.id)}>
              <XIcon size={15} />
            </button>
          </div>
        )
      })}
    </div>,
    document.body,
  )
}

/** A centred dialog for confirmations — destructive ones get `danger`. */
export function Modal({
  open,
  title,
  children,
  onClose,
  actions,
}: {
  open: boolean
  title: string
  children: ReactNode
  onClose: () => void
  actions: ReactNode
}): ReactNode {
  const headingId = useId()

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <>
      <button type="button" className="drawer-backdrop" aria-label="Close dialog" onClick={onClose} />
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={headingId}>
        <h2 id={headingId}>{title}</h2>
        <div className="modal__body">{children}</div>
        <div className="modal__actions">{actions}</div>
      </div>
    </>,
    document.body,
  )
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  danger,
  pending,
  onConfirm,
  onClose,
}: {
  open: boolean
  title: string
  message: ReactNode
  confirmLabel: string
  danger?: boolean
  pending?: boolean
  onConfirm: () => void
  onClose: () => void
}): ReactNode {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            type="button"
            className={danger ? 'btn btn--primary btn--destructive' : 'btn btn--primary'}
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      {message}
    </Modal>
  )
}
